import {skipToken} from '@reduxjs/toolkit/query';
import {AlertProps} from 'platform/components';

import {isNil, isNotNil, not} from 'ramda';
import {isPositive} from 'ramda-adjunct';

import {useGetServiceCaseByVehicleCountQuery} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {vehiclesRoutes} from '@dms/routes';

import {composePath, Nullish} from 'shared/web';

import {getOtherActiveServiceCasesCount} from '../utils/getOtherActiveServiceCasesCount';

interface UseActiveServiceCasesAlertProps {
  vehicleId: string | Nullish;
  /** The create flow's own draft counts itself towards the active total. */
  hasOwnDraft: boolean;
  isEnabled: boolean;
}

export function useActiveServiceCasesAlert(
  props: UseActiveServiceCasesAlertProps
): AlertProps | undefined {
  const {data: activeServiceCases} = useGetServiceCaseByVehicleCountQuery(
    props.isEnabled && isNotNil(props.vehicleId)
      ? {vehicleId: props.vehicleId, status: 'ACTIVE'}
      : skipToken
  );

  const otherActiveServiceCasesCount = getOtherActiveServiceCasesCount(
    activeServiceCases?.count,
    props.hasOwnDraft
  );

  if (isNil(props.vehicleId) || not(isPositive(otherActiveServiceCasesCount))) {
    return undefined;
  }

  return {
    type: 'banner',
    variant: 'warning',
    title: i18n.t('entity.serviceCase.labels.activeServiceCasesNoticeTitle'),
    message: i18n.t('entity.serviceCase.labels.activeServiceCasesNotice', {
      count: otherActiveServiceCasesCount,
    }),
    hyperlinks: [
      {
        title: i18n.t('entity.serviceCase.actions.showServiceCases'),
        href: composePath(vehiclesRoutes.activeServiceCases, {
          params: {id: props.vehicleId},
          // The new tab must not inherit the create wizard's own query params.
          isSearchParamsPreserved: false,
        }),
        target: '_blank',
        rel: 'noopener noreferrer',
      },
    ],
  };
}
